import { Tooltip } from 'antd';
import React from 'react';
import styled from 'styled-components';
import { theme } from '../../theme/semanticTokens';
import { TextTransform } from '../types';

export type TagVariant =
  | 'positivePrimary'
  | 'warningPrimary'
  | 'criticalPrimary'
  | 'neutralPrimary'
  | 'neutralSecondary'
  | 'positiveSecondary'
  | 'criticalSecondary'
  | 'warningSecondary'
  | 'blueSecondary'
  | 'yellowSecondary'
  | 'purpleSecondary'
  | 'pinkSecondary'
  | 'cyanSecondary'
  | 'graySecondary'
  | 'positivePrimaryRounded'
  | 'infoPrimaryRounded'
  | 'criticalPrimaryRounded'
  | 'warningPrimaryRounded'
  | 'neutralPrimaryRounded'
  | 'neutralSecondaryRounded';

export type TagProps = {
  variant: TagVariant;
  text: string;
  fontWeight?: '400' | '500' | '600' | '700' | 400 | 500 | 600 | 700;
  textTransform?: TextTransform;
  tooltipText?: string;
  tooltipPlacement?: 'top' | 'left' | 'right' | 'bottom';
  'data-testid'?: string;
};

const TagContainer = styled.div`
  display: inline-flex;
  align-items: center;
  height: 24px;
  line-height: 20px;
  letter-spacing: 0.24px;
  cursor: default;
  user-select: none;

  &.primary {
    border-radius: 4px;
    color: ${theme.dime.colors.text.primary};
    background-color: ${theme.dime.colors.fill.secondary};
  }

  &.positivePrimary {
    border-radius: 4px;
    color: ${theme.dime.colors.text.inverse};
    background-color: ${theme.dime.colors.fill.successPrimary};
  }
  &.warningPrimary {
    border-radius: 4px;
    color: ${theme.dime.colors.text.primary};
    background-color: ${theme.dime.colors.fill.warningPrimary};
  }
  &.criticalPrimary {
    border-radius: 4px;
    color: ${theme.dime.colors.text.inverse};
    background-color: ${theme.dime.colors.fill.criticalPrimary};
  }
  &.neutralPrimary {
    border-radius: 4px;
    color: ${theme.dime.colors.text.inverse};
    background-color: ${theme.dime.colors.fill.decorative10};
  }
  &.neutralSecondary {
    height: 22px;
    border-radius: 4px;
    color: ${theme.dime.colors.text.primary};
    background-color: ${theme.dime.colors.fill.secondary};
  }
  &.positiveSecondary {
    height: 22px;
    border-radius: 4px;
    color: ${theme.dime.colors.text.successOnColor};
    background-color: ${theme.dime.colors.fill.successSecondary};
  }
  &.warningSecondary {
    height: 22px;
    border-radius: 4px;
    color: ${theme.dime.colors.text.primary};
    background-color: ${theme.dime.colors.fill.warningSecondary};
  }
  &.criticalSecondary {
    height: 22px;
    border-radius: 4px;
    color: ${theme.dime.colors.text.criticalOnColor};
    background-color: ${theme.dime.colors.fill.criticalSecondary};
  }
  &.blueSecondary {
    border-radius: 4px;
    color: ${theme.dime.colors.text.decorative12};
    background-color: ${theme.dime.colors.fill.decorative11};
  }
  &.yellowSecondary {
    height: 22px;
    border-radius: 4px;
    color: ${theme.dime.colors.text.decorative2};
    background-color: ${theme.dime.colors.fill.decorative1};
  }
  &.purpleSecondary {
    height: 22px;
    border-radius: 4px;
    color: ${theme.dime.colors.text.decorative4};
    background-color: ${theme.dime.colors.fill.decorative3};
    font-weight: 400;
  }
  &.pinkSecondary {
    height: 22px;
    border-radius: 4px;
    color: ${theme.dime.colors.text.decorative6};
    background-color: ${theme.dime.colors.fill.decorative5};
  }
  &.cyanSecondary {
    height: 22px;
    border-radius: 4px;
    color: ${theme.dime.colors.text.primary};
    background-color: ${theme.dime.colors.fill.decorative7};
  }
  &.graySecondary {
    border-radius: 4px;
    color: ${theme.dime.colors.text.primary};
    background-color: ${theme.dime.colors.fill.secondary};
  }

  &.positivePrimaryRounded {
    height: 18px;
    border-radius: 100px;
    color: ${theme.dime.colors.text.inverse};
    background-color: ${theme.dime.colors.fill.successPrimary};
  }
  &.infoPrimaryRounded {
    height: 18px;
    border-radius: 100px;
    color: ${theme.dime.colors.text.inverse};
    background-color: ${theme.dime.colors.fill.infoPrimary};
  }
  &.warningPrimaryRounded {
    height: 18px;
    border-radius: 100px;
    color: ${theme.dime.colors.text.primary};
    background-color: ${theme.dime.colors.fill.warningPrimary};
  }
  &.neutralPrimaryRounded {
    height: 18px;
    border-radius: 100px;
    color: ${theme.dime.colors.text.inverse};
    background-color: ${theme.dime.colors.fill.decorative10};
  }
  &.neutralSecondaryRounded {
    height: 18px;
    border-radius: 100px;
    color: ${theme.dime.colors.text.primary};
    background-color: ${theme.dime.colors.fill.secondary};
  }
  &.criticalPrimaryRounded {
    height: 18px;
    border-radius: 100px;
    color: ${theme.dime.colors.text.inverse};
    background-color: ${theme.dime.colors.fill.criticalPrimary};
  }
`;

const TextWrapper = styled.span<{
  textTransform?: TextTransform;
  fontWeight?: '400' | '500' | '600' | '700' | 400 | 500 | 600 | 700;
}>`
  font-size: ${theme.dime.fonts.sizes.xs};
  letter-spacing: 0.24px;
  padding: ${theme.dime.spacing.s} ${theme.dime.spacing.m};
  text-transform: ${(props) => props.textTransform || 'uppercase'};
  font-weight: ${(props) => props.fontWeight || '600'};
`;

export const TagLabel: React.FC<TagProps> = ({
  variant = 'neutralPrimary',
  text,
  textTransform,
  tooltipText,
  tooltipPlacement = 'top',
  fontWeight,
  'data-testid': testId
}) => {
  const tagContent = (
    <TagContainer className={variant} data-testid={testId ?? 'tag-label'}>
      <TextWrapper
        className={variant}
        textTransform={textTransform}
        fontWeight={fontWeight}
      >
        {text}
      </TextWrapper>
    </TagContainer>
  );
  return tooltipText ? (
    <Tooltip title={tooltipText} placement={tooltipPlacement}>
      {tagContent}
    </Tooltip>
  ) : (
    tagContent
  );
};
